import { useState } from 'preact/hooks';
import type { Api } from '../api.js';
import { dm, dmSv } from '../format.js';
import { texts, useLang } from '../i18n.js';
import { Dialog, toast } from '../ui.js';

/**
 * Inviting a client's contacts to the client portal (owner 2026-10-28, "Knapp + förslag vid avtal"). Each contact on the
 * client card shows whether they have a login and has Invite; once the client has a signed agreement the card suggests
 * the contacts who have no link yet. Clients sign in with their personal link or a code by email, never a password.
 */

export interface PortalAccess {
  state: 'none' | 'invited' | 'in';
  at: string | null;
  lastUsedAt: string | null;
}
interface InviteOut {
  contactId: string;
  to: string;
  emailed: boolean;
  error: string | null;
  subject: string;
  body: string;
  access: PortalAccess;
}
interface Who {
  id: string;
  name: string | null;
  email: string;
}

const TX = texts(
  {
    none: 'No portal login yet',
    invited: (d: string) => `Invited to the portal ${d}, not signed in yet`,
    signedIn: (d: string) => `Signed in to the portal ${d}`,
    invite: 'Invite',
    again: 'Invite again',
    inviteFor: (n: string) => `Invite ${n} to the client portal`,
    suggestTitle: 'Invite to the client portal',
    suggestBody: (names: string) => `The client has an agreement, but ${names} ${names.includes(' and ') ? 'have' : 'has'} no login yet. They get an email from KFF with their personal link; replies come to you.`,
    send: 'Send invitations',
    sendOne: 'Send invitation',
    later: 'Not now',
    sent: (n: string) => `Invitation sent to ${n}`,
    copyTitle: (n: string) => `Send the invitation to ${n} yourself`,
    copyWhy: (e: string | null): string => (e === 'not_allowed_here' ? 'This environment does not email that address, so copy the text and send it yourself.' : 'The Hub cannot send email right now, so copy the text and send it yourself.'),
    subject: 'Subject',
    text: 'Text',
    copy: 'Copy text',
    copied: 'Copied',
    close: 'Close',
    and: ' and '
  },
  {
    none: 'Ingen portalinloggning än',
    invited: (d: string) => `Inbjuden till portalen ${d}, inte inloggad än`,
    signedIn: (d: string) => `Inloggad i portalen ${d}`,
    invite: 'Bjud in',
    again: 'Bjud in igen',
    inviteFor: (n: string) => `Bjud in ${n} till kundportalen`,
    suggestTitle: 'Bjud in till kundportalen',
    suggestBody: (names: string) => `Kunden har avtal, men ${names} har ingen inloggning än. De får ett mejl från KFF med sin personliga länk, och svar kommer till dig.`,
    send: 'Skicka inbjudningar',
    sendOne: 'Skicka inbjudan',
    later: 'Inte nu',
    sent: (n: string) => `Inbjudan skickad till ${n}`,
    copyTitle: (n: string) => `Skicka inbjudan till ${n} själv`,
    copyWhy: (e: string | null) => (e === 'not_allowed_here' ? 'Den här miljön mejlar inte till den adressen, så kopiera texten och skicka den själv.' : 'Hubben kan inte skicka mejl just nu, så kopiera texten och skicka den själv.'),
    subject: 'Ämne',
    text: 'Text',
    copy: 'Kopiera texten',
    copied: 'Kopierat',
    close: 'Stäng',
    and: ' och '
  }
);

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));
const nameOf = (c: Who) => c.name ?? c.email;
const LATER = (clientId: string) => `kff.portalInvite.later.${clientId}`;

/** One contact's portal line on the client card. */
export function PortalAccessLine(p: { access: PortalAccess }) {
  const lang = useLang();
  const t = TX[lang];
  const d = (iso: string) => (lang === 'sv' ? dmSv : dm)(iso.slice(0, 10));
  const a = p.access;
  return <span class={`small ${a.state === 'none' ? 'muted' : 'cc-portal-ok'}`}>{a.state === 'in' && a.lastUsedAt ? t.signedIn(d(a.lastUsedAt)) : a.state === 'invited' && a.at ? t.invited(d(a.at)) : t.none}</span>;
}

/** Sends the invitations; a mail the Hub could not send comes back as a text to copy. */
export function useInvite(api: Api, clientId: string, onDone: (out: InviteOut[]) => void) {
  const t = TX[useLang()];
  const [busy, setBusy] = useState(false);
  const [copy, setCopy] = useState<{ who: Who; out: InviteOut; done: InviteOut[] } | null>(null);
  const send = async (who: Who[]) => {
    setBusy(true);
    const done: InviteOut[] = [];
    let unsent: { who: Who; out: InviteOut } | null = null;
    try {
      for (const c of who) {
        const out = await api.post<InviteOut>(`/api/clients/${clientId}/contacts/${c.id}/portal-invite`);
        done.push(out);
        if (!out.emailed) unsent = { who: c, out };
      }
      const mailed = who.filter((c) => done.find((o) => o.contactId === c.id)?.emailed);
      if (mailed.length) toast({ text: t.sent(mailed.map(nameOf).join(t.and)) });
    } catch (e) {
      toast({ text: errText(e) });
    } finally {
      setBusy(false);
      // the card reloads when the person is done: with a text to copy, once they close it (the reload would hide it)
      if (unsent) setCopy({ ...unsent, done });
      else if (done.length) onDone(done);
    }
  };
  const close = () => {
    if (copy) onDone(copy.done);
    setCopy(null);
  };
  const dialog = copy ? <CopyInvite who={copy.who} out={copy.out} onClose={close} /> : null;
  return { busy, send, dialog };
}

function CopyInvite(p: { who: Who; out: InviteOut; onClose: () => void }) {
  const t = TX[useLang()];
  const [copied, setCopied] = useState(false);
  const run = async () => {
    try {
      await navigator.clipboard?.writeText(`${p.out.subject}\n\n${p.out.body}`);
      setCopied(true);
    } catch {
      /* the text is selectable */
    }
  };
  return (
    <Dialog
      title={t.copyTitle(nameOf(p.who))}
      onClose={p.onClose}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {t.close}
          </button>
          <button type="button" class="btn btn-primary" onClick={() => void run()}>
            {copied ? t.copied : t.copy}
          </button>
        </>
      }
    >
      <div class="stack">
        <p class="small muted">{t.copyWhy(p.out.error)}</p>
        <div class="field">
          <label class="label" for="pi-subject">
            {t.subject}
          </label>
          <input class="input input--wide" id="pi-subject" readOnly value={p.out.subject} />
        </div>
        <div class="field">
          <label class="label" for="pi-body">
            {t.text}
          </label>
          <textarea class="input input--wide" id="pi-body" rows={10} readOnly value={`${p.out.to}\n\n${p.out.body}`} />
        </div>
      </div>
    </Dialog>
  );
}

/** The client card's suggestion: the client has an agreement and these contacts have no login yet. */
export function PortalInviteSuggestion(p: { api: Api; clientId: string; contacts: Who[]; onDone: (out: InviteOut[]) => void }) {
  const t = TX[useLang()];
  const [later, setLater] = useState(() => {
    try {
      return localStorage.getItem(LATER(p.clientId)) === p.contacts.map((c) => c.id).join(',');
    } catch {
      return false;
    }
  });
  const inv = useInvite(p.api, p.clientId, p.onDone);
  if (!p.contacts.length || later) return inv.dialog;
  const names = p.contacts.map(nameOf);
  const list = names.length > 1 ? `${names.slice(0, -1).join(', ')}${t.and}${names.at(-1)}` : names[0]!;
  const hide = () => {
    try {
      localStorage.setItem(LATER(p.clientId), p.contacts.map((c) => c.id).join(','));
    } catch {
      /* hidden for now only */
    }
    setLater(true);
  };
  return (
    <div class="callout callout--info cc-invite" role="status">
      <strong>{t.suggestTitle}</strong>
      <div>{t.suggestBody(list)}</div>
      <div class="cluster">
        <button type="button" class="btn btn-primary btn-sm" disabled={inv.busy} onClick={() => void inv.send(p.contacts)}>
          {p.contacts.length > 1 ? t.send : t.sendOne}
        </button>
        <button type="button" class="btn btn-ghost btn-sm" onClick={hide}>
          {t.later}
        </button>
      </div>
      {inv.dialog}
    </div>
  );
}

/** Invite (or invite again) for one contact. */
export function InviteButton(p: { api: Api; clientId: string; contact: Who; access: PortalAccess; onDone: (out: InviteOut[]) => void }) {
  const t = TX[useLang()];
  const inv = useInvite(p.api, p.clientId, p.onDone);
  return (
    <>
      <button type="button" class="btn btn-ghost btn-sm" disabled={inv.busy} aria-label={t.inviteFor(nameOf(p.contact))} onClick={() => void inv.send([p.contact])}>
        {p.access.state === 'none' ? t.invite : t.again}
      </button>
      {inv.dialog}
    </>
  );
}
